<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="ns-form">
    <header class="ns-form__header">
      <button
        type="button"
        class="ns-form__back"
        aria-label="Torna al mese"
        @click="onCancel"
      >
        ‹
      </button>
      <h1>{{ tripId ? 'Modifica trasferta' : 'Nuova trasferta' }}</h1>
    </header>

    <div v-if="loadState === 'loading'" class="ns-form__loading">
      Caricamento della trasferta…
    </div>

    <div v-else-if="loadState === 'error'" class="ns-form__load-error">
      <p>{{ loadError }}</p>
      <button type="button" class="ns-btn ns-btn--secondary" @click="load">
        Riprova
      </button>
    </div>

    <form v-else class="ns-form__body" novalidate @submit.prevent="onSubmit">
      <p v-if="draftRestored" class="ns-form__note">
        Hai ripreso la trasferta che non avevi salvato.
        <button type="button" @click="onDiscardDraft">Ricomincia</button>
      </p>

      <p v-if="vehicleOptions.length === 0" class="ns-form__blocker">
        Non hai veicoli registrati: chiedi all'ufficio del personale di
        aggiungerne uno.
      </p>

      <div class="ns-field">
        <label for="ns-trip-date">Data</label>
        <input id="ns-trip-date" v-model="form.date" type="date" required />
      </div>

      <div v-if="vehicleOptions.length > 1" class="ns-field">
        <label for="ns-trip-vehicle">Veicolo</label>
        <select id="ns-trip-vehicle" v-model.number="form.vehicleId">
          <option
            v-for="vehicle in vehicleOptions"
            :key="vehicle.id"
            :value="vehicle.id"
          >
            {{ vehicle.text }}
          </option>
        </select>
      </div>

      <place-field
        v-model="form.origin"
        label="Partenza"
        :recent="recent"
      ></place-field>

      <div class="ns-trip__swap-row">
        <button
          type="button"
          class="ns-trip__swap"
          :disabled="!form.origin && !form.destination"
          @click="onSwap"
        >
          ⇅ Inverti partenza e arrivo
        </button>
      </div>

      <place-field
        v-model="form.destination"
        label="Arrivo"
        :recent="recent"
      ></place-field>

      <label class="ns-toggle">
        <input v-model="form.roundTrip" type="checkbox" role="switch" />
        <span class="ns-toggle__track" aria-hidden="true"></span>
        <span>Andata e ritorno</span>
      </label>

      <div
        v-if="form.origin && form.destination"
        class="ns-trip__distance"
        :class="{'ns-trip__distance--error': !!distanceError}"
        aria-live="polite"
      >
        <template v-if="distanceLoading">Calcolo dei km…</template>
        <template v-else-if="distanceError">{{ distanceError }}</template>
        <template v-else-if="oneWayTenths !== null">
          <strong>{{ distanceText }}</strong>
          <small v-if="distanceIsSaved">
            Km registrati: si ricalcolano solo se cambi partenza, arrivo o
            andata e ritorno.
          </small>
          <small v-else>
            L'importo lo calcola il server con la tariffa del veicolo: lo vedi
            nel riepilogo del mese dopo il salvataggio.
          </small>
        </template>
      </div>

      <div class="ns-field">
        <label for="ns-trip-purpose">Motivo</label>
        <textarea
          id="ns-trip-purpose"
          v-model="form.purpose"
          rows="3"
          maxlength="500"
          placeholder="Es. incontro con il cliente, sopralluogo in cantiere"
        ></textarea>
      </div>

      <p v-if="error" ref="error" class="ns-form__error" role="alert">
        {{ error }}
      </p>

      <div class="ns-form__actions">
        <button
          type="submit"
          class="ns-btn ns-btn--primary"
          :disabled="busy || vehicleOptions.length === 0"
        >
          {{ saving ? 'Salvataggio…' : 'Salva trasferta' }}
        </button>
        <button
          v-if="tripId"
          type="button"
          class="ns-btn ns-btn--danger"
          :disabled="busy"
          @click="onDelete"
        >
          {{ deleting ? 'Eliminazione…' : 'Elimina' }}
        </button>
        <button type="button" class="ns-btn ns-btn--ghost" @click="onCancel">
          Annulla
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import {nextTick} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import {
  NOTE_SPESE_API_PATH,
  inlineErrorMessage,
} from '@/orangehrmNoteSpesePlugin/util/apiError';
import {
  clearDraft,
  loadDraft,
  saveDraft,
} from '@/orangehrmNoteSpesePlugin/util/draft';
import {
  decimalToTenths,
  formatKm,
  tenthsToDecimal,
} from '@/orangehrmNoteSpesePlugin/util/money';
import PlaceField from '@/orangehrmNoteSpesePlugin/components/mobile/PlaceField.vue';

// La bozza vale solo per una trasferta nuova: in modifica i dati buoni sono
// quelli sul server, e una bozza vecchia li sovrascriverebbe senza dirlo.
const DRAFT_KEY = 'trip-new';

// Solo i tre campi che GeoPoint::fromArray() legge.
function point(place) {
  return place ? {label: place.label, lat: place.lat, lon: place.lon} : null;
}

function routeKey(origin, destination) {
  if (!origin || !destination) return null;
  return JSON.stringify([point(origin), point(destination)]);
}

export default {
  name: 'TripForm',

  components: {
    'place-field': PlaceField,
  },

  props: {
    profile: {
      type: Object,
      required: true,
    },
    recent: {
      type: Array,
      default: () => [],
    },
    tripId: {
      type: Number,
      default: null,
    },
    defaultDate: {
      type: String,
      required: true,
    },
  },

  emits: ['saved', 'cancel'],

  setup() {
    const baseUrl = window.appGlobal.baseUrl;
    const tripsHttp = new APIService(baseUrl, '/api/v2/note-spese/trips');
    const distanceHttp = new APIService(
      baseUrl,
      '/api/v2/note-spese/route-distance',
    );
    tripsHttp.setIgnorePath(NOTE_SPESE_API_PATH);
    distanceHttp.setIgnorePath(NOTE_SPESE_API_PATH);

    return {tripsHttp, distanceHttp};
  },

  data() {
    return {
      loadState: this.tripId ? 'loading' : 'ready',
      loadError: '',
      form: null,
      initialJson: '',
      draftRestored: false,
      // Veicolo della trasferta caricata, anche se nel frattempo è stato
      // disattivato e quindi manca da profile.vehicles.
      loadedVehicleId: null,
      loadedDate: null,
      // Km della trasferta salvata e il percorso a cui si riferiscono: finché
      // partenza e arrivo non cambiano non serve chiamare OpenRouteService.
      savedRouteKey: null,
      savedOneWayTenths: null,
      savedRoundTrip: null,
      oneWayTenths: null,
      distanceLoading: false,
      distanceError: '',
      distanceRequest: 0,
      error: '',
      saving: false,
      deleting: false,
    };
  },

  computed: {
    busy() {
      return this.saving || this.deleting;
    },

    vehicleOptions() {
      const options = this.profile.vehicles.map((vehicle) => ({
        id: vehicle.id,
        text: vehicle.plate
          ? `${vehicle.description} (${vehicle.plate})`
          : vehicle.description,
      }));
      if (
        this.loadedVehicleId !== null &&
        !options.some((option) => option.id === this.loadedVehicleId)
      ) {
        options.push({
          id: this.loadedVehicleId,
          text: 'Veicolo non più attivo',
        });
      }
      return options;
    },

    defaultVehicleId() {
      const ids = this.profile.vehicles.map((vehicle) => vehicle.id);
      if (ids.includes(this.profile.lastVehicleId)) {
        return this.profile.lastVehicleId;
      }
      return ids.length > 0 ? ids[0] : null;
    },

    currentRouteKey() {
      return this.form
        ? routeKey(this.form.origin, this.form.destination)
        : null;
    },

    distanceIsSaved() {
      return (
        this.savedRouteKey !== null &&
        this.currentRouteKey === this.savedRouteKey &&
        this.form.roundTrip === this.savedRoundTrip
      );
    },

    // "47,3 km × 2 = 94,6 km": i decimi sono interi, niente float.
    distanceText() {
      const oneWay = formatKm(tenthsToDecimal(this.oneWayTenths));
      if (!this.form.roundTrip) return oneWay;
      return `${oneWay} × 2 = ${formatKm(
        tenthsToDecimal(this.oneWayTenths * 2),
      )}`;
    },

    isDirty() {
      return !!this.form && JSON.stringify(this.form) !== this.initialJson;
    },
  },

  watch: {
    currentRouteKey(key) {
      if (this.loadState !== 'ready') return;
      this.onRouteChanged(key);
    },

    form: {
      deep: true,
      handler() {
        if (this.tripId || this.loadState !== 'ready') return;
        if (this.isDirty) {
          saveDraft(DRAFT_KEY, this.form);
        } else {
          clearDraft(DRAFT_KEY);
        }
      },
    },
  },

  created() {
    this.load();
  },

  methods: {
    defaults() {
      return {
        date: this.defaultDate,
        vehicleId: this.defaultVehicleId,
        origin: null,
        destination: null,
        roundTrip: false,
        purpose: '',
      };
    },

    async load() {
      if (!this.tripId) {
        const defaults = this.defaults();
        this.initialJson = JSON.stringify(defaults);
        const draft = loadDraft(DRAFT_KEY);
        this.form = draft ? this.fromDraft(draft, defaults) : defaults;
        this.draftRestored = !!draft;
        // Se la bozza ha già partenza e arrivo, i km li chiede il watcher
        // di currentRouteKey, che scatta subito dopo.
        this.loadState = 'ready';
        return;
      }

      this.loadState = 'loading';
      try {
        const response = await this.tripsHttp.get(this.tripId);
        const trip = response.data.data;
        this.form = {
          date: trip.date,
          vehicleId: trip.vehicleId,
          origin: point(trip.origin),
          destination: point(trip.destination),
          roundTrip: trip.roundTrip,
          purpose: trip.purpose,
        };
        this.initialJson = JSON.stringify(this.form);
        this.loadedVehicleId = trip.vehicleId;
        this.loadedDate = trip.date;

        const savedTenths = decimalToTenths(String(trip.km));
        if (savedTenths !== null) {
          this.savedRouteKey = this.currentRouteKey;
          this.savedRoundTrip = trip.roundTrip;
          this.savedOneWayTenths = trip.roundTrip
            ? Math.round(savedTenths / 2)
            : savedTenths;
          this.oneWayTenths = this.savedOneWayTenths;
        }
        this.loadState = 'ready';
      } catch (error) {
        this.loadError =
          inlineErrorMessage(error) || 'Non riesco a caricare la trasferta.';
        this.loadState = 'error';
      }
    },

    // Una bozza di una versione precedente o con un veicolo non più
    // disponibile non deve rompere il modulo: si prende solo ciò che vale.
    fromDraft(draft, defaults) {
      const form = {...defaults};
      Object.keys(defaults).forEach((key) => {
        if (draft[key] !== undefined) form[key] = draft[key];
      });
      if (!this.profile.vehicles.some((v) => v.id === form.vehicleId)) {
        form.vehicleId = defaults.vehicleId;
      }
      form.origin = point(form.origin);
      form.destination = point(form.destination);
      form.roundTrip = !!form.roundTrip;
      form.purpose = String(form.purpose || '');
      return form;
    },

    async onRouteChanged(key) {
      const request = ++this.distanceRequest;
      this.distanceError = '';
      if (!key) {
        this.oneWayTenths = null;
        this.distanceLoading = false;
        return;
      }
      if (key === this.savedRouteKey) {
        this.oneWayTenths = this.savedOneWayTenths;
        this.distanceLoading = false;
        return;
      }

      // Sempre sola andata: l'andata e ritorno è il doppio esatto (così fa
      // anche il server), e cambiare l'interruttore non richiede un'altra
      // chiamata a OpenRouteService.
      this.distanceLoading = true;
      try {
        const response = await this.distanceHttp.create({
          from: point(this.form.origin),
          to: point(this.form.destination),
          roundTrip: false,
        });
        if (request !== this.distanceRequest) return;
        this.oneWayTenths = response.data.data.kmTenths;
      } catch (error) {
        if (request !== this.distanceRequest) return;
        this.oneWayTenths = null;
        this.distanceError =
          inlineErrorMessage(error) ||
          'Non riesco a calcolare i km adesso: riprova a salvare tra poco.';
      } finally {
        if (request === this.distanceRequest) this.distanceLoading = false;
      }
    },

    onSwap() {
      const {origin, destination} = this.form;
      this.form.origin = destination;
      this.form.destination = origin;
    },

    onDiscardDraft() {
      clearDraft(DRAFT_KEY);
      this.form = this.defaults();
      this.draftRestored = false;
      this.error = '';
    },

    missingFields() {
      const missing = [];
      if (!this.form.date) missing.push('data');
      if (!this.form.vehicleId) missing.push('veicolo');
      if (!this.form.origin) missing.push('partenza');
      if (!this.form.destination) missing.push('arrivo');
      if (!this.form.purpose.trim()) missing.push('motivo');
      return missing;
    },

    showError(message) {
      this.error = message;
      nextTick(() => {
        if (this.$refs.error) {
          this.$refs.error.scrollIntoView({
            block: 'center',
            behavior: 'smooth',
          });
        }
      });
    },

    async onSubmit() {
      if (this.busy) return;
      const missing = this.missingFields();
      if (missing.length > 0) {
        this.showError(`Da completare: ${missing.join(', ')}.`);
        return;
      }

      // Esattamente i parametri dichiarati da DirectorTripAPI: un campo in
      // più farebbe rispondere 422.
      const body = {
        date: this.form.date,
        vehicleId: this.form.vehicleId,
        origin: point(this.form.origin),
        destination: point(this.form.destination),
        roundTrip: this.form.roundTrip,
        purpose: this.form.purpose.trim(),
      };

      this.saving = true;
      this.error = '';
      try {
        const response = this.tripId
          ? await this.tripsHttp.update(this.tripId, body)
          : await this.tripsHttp.create(body);
        if (!this.tripId) clearDraft(DRAFT_KEY);
        this.$emit('saved', {
          date: response.data.data.date,
          message: this.tripId ? 'Trasferta aggiornata.' : 'Trasferta salvata.',
          placesChanged: true,
        });
      } catch (error) {
        // Il modulo resta com'è: si corregge e si riprova.
        const message = inlineErrorMessage(error);
        if (message) this.showError(message);
      } finally {
        this.saving = false;
      }
    },

    async onDelete() {
      if (this.busy) return;
      if (!window.confirm('Eliminare questa trasferta?')) return;

      this.deleting = true;
      this.error = '';
      try {
        await this.tripsHttp.delete(this.tripId);
        this.$emit('saved', {
          date: this.loadedDate,
          message: 'Trasferta eliminata.',
          placesChanged: true,
        });
      } catch (error) {
        const message = inlineErrorMessage(error);
        if (message) this.showError(message);
      } finally {
        this.deleting = false;
      }
    },

    // "Annulla" e la freccia scartano davvero: si chiede conferma se c'è
    // qualcosa da perdere. Il "indietro" del telefono invece lascia la bozza.
    onCancel() {
      if (
        this.isDirty &&
        !this.busy &&
        !window.confirm('Uscire senza salvare? Le modifiche andranno perse.')
      ) {
        return;
      }
      if (!this.tripId) clearDraft(DRAFT_KEY);
      this.$emit('cancel');
    },
  },
};
</script>

<style scoped src="./mobileForm.css"></style>

<style scoped>
.ns-trip__swap-row {
  display: flex;
  justify-content: center;
  margin: -8px 0 8px;
}

.ns-trip__swap {
  min-height: 44px;
  padding: 0 16px;
  border: 0;
  border-radius: 22px;
  background: #eef0f2;
  color: #1d2129;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
}

.ns-trip__swap:disabled {
  opacity: 0.45;
}

.ns-trip__distance {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0 0 16px;
  padding: 14px;
  border-radius: 12px;
  background: #ffffff;
  border: 1.5px solid #d7dae0;
}

.ns-trip__distance strong {
  font-size: 22px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.ns-trip__distance small {
  font-size: 14px;
  color: var(--ns-muted);
}

.ns-trip__distance--error {
  background: #fdecea;
  border-color: #f1b0aa;
  color: #8c1d18;
  font-weight: 600;
}
</style>
